import React, { useEffect, useState, useCallback, useRef, Component, ErrorInfo, ReactNode, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { client } from './lib/amplifyClient';
import { findBestUserRowByEmail, pickUserRecordForSession } from './lib/resolveAppUser';
import { ensureAppUserFromInvitation, resolveExistingAppUserId } from './lib/ensureAppUser';
import { touchLastLogin } from './lib/touchLastLogin';
import { freelancerNeedsCheckout } from './lib/freelancerCheckoutGate';
import { listKidProfilesForParentSession } from './lib/parentKidProfiles';
import {
  redirectClinicianToDaycareApp,
  shouldRedirectClinicianToDaycare,
} from './lib/tinywinsDaycareRedirect';
import { isSchoolRole, redirectToSchoolApp } from './lib/schoolAppUrl';
import {
  redirectParentToTinyWinsWithSso,
  redirectTherapistToMiraPlusWithSso,
  shouldRedirectParentToTinyWins,
  shouldRedirectTherapistToMiraPlus,
} from './lib/miraplusAppUrl';
import { VINAYAKA_MISSION_LOGIN_PATH } from './constants/vinayakaMissionBrand';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { getPendingAssignmentForParent } from './lib/parentQuestionnaireAssignments';
import {
  attributeProgramReferralSignup,
  captureProgramReferralFromSearch,
  currentProductSurface,
} from './lib/programReferral';

const POST_AUTH_RETURN_KEY = 'postAuthReturnTo';

async function tryAttributeProgramReferral(userId: string): Promise<void> {
  try {
    const userRes = await client.models.User.get({ id: userId }, { authMode: 'apiKey' });
    const row = userRes.data;
    const name = [row?.fName, row?.lName].filter(Boolean).join(' ').trim();
    await attributeProgramReferralSignup({
      referredUserId: userId,
      referredRole: row?.role ?? null,
      referredEmail: row?.email ?? null,
      referredName: name || null,
      productSurface: currentProductSurface(),
    });
  } catch (err) {
    console.warn('Program referral attribution skipped:', err);
  }
}
function getPostAuthReturnTo(): string | null {
  try {
    return sessionStorage.getItem(POST_AUTH_RETURN_KEY) ?? localStorage.getItem(POST_AUTH_RETURN_KEY);
  } catch {
    return null;
  }
}
function clearPostAuthReturnTo(): void {
  try {
    sessionStorage.removeItem(POST_AUTH_RETURN_KEY);
    localStorage.removeItem(POST_AUTH_RETURN_KEY);
  } catch {
    // ignore
  }
}

/** Short /social-stories/:slug → canonical /social-stories/view/:slug */
function SocialStoryShortPathRedirect() {
  const { storySlug } = useParams<{ storySlug: string }>();
  return <Navigate to={`/social-stories/view/${encodeURIComponent(storySlug || '')}`} replace />;
}

/** `/register?ref=CODE` → home with sign-up modal; preserves program referral code. */
function RegisterWithProgramReferralRedirect() {
  const [searchParams] = useSearchParams();
  captureProgramReferralFromSearch(searchParams);
  const next = new URLSearchParams();
  next.set('openAuth', 'signUp');
  const ref = searchParams.get('ref') || searchParams.get('programRef');
  if (ref) next.set('ref', ref);
  return <Navigate to={`/?${next.toString()}`} replace />;
}

import { Box, Typography, Button } from '@mui/material';
import Header from './components/common/Header';
import FooterModern from './components/common/FooterModern';
import GlobalLayout from './components/common/GlobalLayout';
import CommunityLayout from './components/parent/community/CommunityLayout';
const ReferParentPage = lazy(() => import('./components/ReferParentPage'));
const ProgramReferralPage = lazy(() => import('./components/program-referral/ProgramReferralPage'));
const Landing3D = lazy(() => import('./components/landing-3d/Landing3D'));
const TinyWinsHomeLanding = lazy(() => import('./components/landing/TinyWinsHomeLanding'));
const DemoDashboard = lazy(() => import('./components/admin/DemoDashboard'));
const ProfileSetup = lazy(() => import('./components/auth/ProfileSetup'));
const TeamList = lazy(() => import('./components/team/TeamList'));
const TeamManagement = lazy(() => import('./components/team/TeamManagement'));
const TeamRequest = lazy(() => import('./components/team/TeamRequest'));
const KidProfileHome = lazy(() => import('./components/KidProfileHome').then((m) => ({ default: m.KidProfileHome })));
const ParentHomeDashboard = lazy(() => import('./components/parent/ParentHomeDashboard'));
const ParentKidTeamPage = lazy(() => import('./components/parent/ParentKidTeamPage'));
const MessagesInboxPage = lazy(() => import('./components/messages/MessagesInboxPage'));
const GoalCoachPage = lazy(() => import('./components/goal-coach/GoalCoachPage'));
const ImportantChecklistPage = lazy(() => import('./components/parent/ImportantChecklistPage'));
const ParentAssignmentsPage = lazy(() => import('./components/parent/ParentAssignmentsPage'));
const ParentStrategyBankPage = lazy(() => import('./components/parent/ParentStrategyBankPage'));
const ParentFocusAreasPage = lazy(() => import('./components/parent/ParentFocusAreasPage'));
const CommunityFeedPage = lazy(() => import('./components/parent/community/CommunityFeedPage'));
const CommunityPostPage = lazy(() => import('./components/parent/community/CommunityPostPage'));
const CommunityEditorPage = lazy(() => import('./components/parent/community/CommunityEditorPage'));
const CommunityAuthorPage = lazy(() => import('./components/parent/community/CommunityAuthorPage'));
import ProtectedRoute from './components/common/ProtectedRoute';
const QuestionnaireForm = lazy(() => import('./components/QuestionnaireForm'));
const ParentConcernsForm = lazy(() => import('./components/ParentConcernsForm').then((m) => ({ default: m.ParentConcernsForm })));
const AnayaIntakeForm = lazy(() => import('./components/AnayaIntakeForm'));
const IntakeFormLanding = lazy(() => import('./components/IntakeFormLanding').then((m) => ({ default: m.IntakeFormLanding })));
const ContactForm = lazy(() => import('./components/ContactForm'));
const GamesPage = lazy(() => import('./components/admin').then((m) => ({ default: m.GamesPage })));
const Assessment = lazy(() => import('./components/admin').then((m) => ({ default: m.Assessment })));
const FeedbackForm = lazy(() => import('./components/admin').then((m) => ({ default: m.FeedbackForm })));
const GapAnalysis = lazy(() => import('./components/GapAnalysis').then((m) => ({ default: m.GapAnalysis })));
const AssessmentOptions = lazy(() => import('./components/AssessmentOptions').then((m) => ({ default: m.AssessmentOptions })));
const DailyDiary = lazy(() => import('./components/DailyDiary'));
const AssessmentHistoryPage = lazy(() => import('./components/AssessmentHistoryPage'));
const CheckDelays = lazy(() => import('./components/CheckDelays'));
const ColorHunt = lazy(() => import('./components/games/ColorHunt').then((m) => ({ default: m.ColorHunt })));
const WhatHappensNext = lazy(() => import('./components/games/WhatHappensNext').then((m) => ({ default: m.WhatHappensNext })));
const CompleteTheStory = lazy(() => import('./components/games/CompleteTheStory').then((m) => ({ default: m.CompleteTheStory })));
const CopyMe = lazy(() => import('./components/games/CopyMe').then((m) => ({ default: m.CopyMe })));
const ReadingComprehension = lazy(() => import('./components/games/ReadingComprehension').then((m) => ({ default: m.ReadingComprehension })));
const OpenUpCardsGame = lazy(() => import('./components/games/OpenUpCardsGame').then((m) => ({ default: m.OpenUpCardsGame })));
const SocialStoriesPage = lazy(() => import('./components/games/SocialStoriesPage').then((m) => ({ default: m.SocialStoriesPage })));
const UserProfile = lazy(() => import('./components/UserProfile'));

const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const DlmMasterManagement = lazy(() => import('./components/admin/DlmMasterManagement'));
const DlmAdjustmentManagement = lazy(() => import('./components/admin/DlmAdjustmentManagement'));
const OrgAdminDashboard = lazy(() => import('./components/admin/OrgAdminDashboard'));
const TherapistClientDashboard = lazy(() => import('./components/admin/TherapistClientDashboard'));
const TherapistDashboardPage = lazy(() => import('./components/admin/TherapistDashboardPage'));
const TherapistReferralsPage = lazy(() => import('./components/admin/TherapistReferralsPage'));
const TherapyGoalSelectionPage = lazy(() => import('./components/admin/TherapyGoalSelectionPage'));
const CreateClientPlanWizard = lazy(() => import('./components/admin/CreateClientPlanWizard'));
const CarePlanPage = lazy(() => import('./components/care-plan/CarePlanPage'));
const AssessmentRecorder = lazy(() => import('./components/assessment/AssessmentRecorder'));
const ParentGoalChat = lazy(() => import('./components/goal-coach/ParentGoalChat'));
const IEPAnalysisResults = lazy(() => import('./components/admin/IEPAnalysisResults'));
const AdminMilestoneView = lazy(() => import('./components/admin/AdminMilestoneView'));
const AdminKidMilestones = lazy(() => import('./components/admin/AdminKidMilestones'));
const MilestoneCatalogViewer = lazy(() => import('./components/admin/MilestoneCatalogViewer'));
const BlogList = lazy(() => import('./components/blog/BlogList'));
const OrgUsageDetails = lazy(() => import('./components/admin/OrgUsageDetails'));
const CreateBlogPost = lazy(() => import('./components/blog/CreateBlogPost'));
const BlogPostView = lazy(() => import('./components/blog/BlogPostView'));
import { BlogProvider } from './context/BlogContext';
import { useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';

import LandingParentReferral from './components/admin/LandingParentReferral';
import ParentLandingPage from './components/parent/ParentLandingPage';
import ParentBlogArticlePage from './components/parent/ParentBlogArticlePage';
import LandingTherapist from './components/admin/LandingTherapist';
import LandingOrgAdmin from './components/admin/LandingOrgAdmin';
import LandingOrgReferral from './components/admin/LandingOrgReferral';
import AboutUs from './components/admin/AboutUs';
import PrivacyPolicy from './components/admin/PrivacyPolicy';
import ScrollToTop from './components/common/ScrollToTop';
const TermsOfService = lazy(() => import('./components/admin/TermsOfService'));
const HowItWorks = lazy(() => import('./components/marketing/MarketingHowItWorksPage'));
const Resources = lazy(() => import('./components/admin/Resources'));
const SchoolsAndCountiesPage = lazy(() => import('./components/marketing/SchoolsAndCountiesPage'));
const DevelopmentalMilestoneTrackerPage = lazy(
  () => import('./components/marketing/DevelopmentalMilestoneTrackerPage'),
);
const IepGoalCoachPage = lazy(() => import('./components/marketing/IepGoalCoachPage'));
const SensoryStrategiesPage = lazy(() => import('./components/marketing/SensoryStrategiesPage'));
const DevelopmentalGoalCategoryPage = lazy(
  () => import('./components/marketing/DevelopmentalGoalCategoryPage'),
);
const ResourceCategoryPage = lazy(() => import('./components/marketing/ResourceCategoryPage'));
const SocialStoriesMarketingPage = lazy(
  () => import('./components/marketing/SocialStoriesMarketingPage'),
);
const Support = lazy(() => import('./components/admin/Support'));
const PaymentSuccess = lazy(() => import('./components/pricing').then((m) => ({ default: m.PaymentSuccess })));
const TherapistChoosePlanPage = lazy(() =>
  import('./components/therapist-choose-plan').then((m) => ({ default: m.TherapistChoosePlanPage }))
);
const TherapistMarketplace = lazy(() => import('./components/parent/TherapistMarketplace'));
const TherapistProfile = lazy(() => import('./components/parent/TherapistProfile'));
const OrganizationPublicPage = lazy(() => import('./components/parent/OrganizationPublicPage'));
const OrganizationDirectoryPage = lazy(() => import('./components/parent/OrganizationDirectoryPage'));
import { HelpChatOverlay } from './components/parent/HelpChatOverlay';
const MilestoneTable = lazy(() => import('./components/milestone-detail/MilestoneTable'));
const KidMilestonePathPage = lazy(() => import('./components/KidMilestonePathPage'));
const MilestoneDetailsPage = lazy(() => import('./components/milestone-detail/MilestoneDetailsPage'));
const AddMilestonePage = lazy(() => import('./components/milestone-detail/AddMilestonePage'));
const SessionDetailPage = lazy(() => import('./components/session-detail/SessionDetailPage'));
const SessionGroupDetailPage = lazy(() => import('./components/session-group/SessionGroupDetailPage'));
const SessionHistoryPage = lazy(() => import('./components/session-group/SessionHistoryPage'));
const TeamPage = lazy(() => import('./components/pages/TeamPage'));
const SocialStories = lazy(() => import('./components/SocialStories'));
const SocialStoryView = lazy(() => import('./components/SocialStoryView'));
const MeetingBooking = lazy(() => import('./components/booking/MeetingBooking'));
const ComprehensionLevels = lazy(() => import('./pages/ComprehensionLevels'));
const ComprehensionGatePage = lazy(() => import('./components/comprehension/ComprehensionGatePage'));
const SocialStoriesGatePage = lazy(() => import('./components/social-stories/SocialStoriesGatePage'));
const InternshipApplication = lazy(() => import('./pages/InternshipApplication'));
const InternPending = lazy(() => import('./pages/InternPending'));
const DeleteMyAccount = lazy(() => import('./components/account/DeleteMyAccount'));
const DeleteMyData = lazy(() => import('./components/account/DeleteMyData'));

const RoleSelection = lazy(() => import('./components/auth/RoleSelection'));
const ProviderVerification = lazy(() => import('./components/auth/ProviderVerification'));
const PendingVerificationNotice = lazy(() => import('./components/auth/PendingVerificationNotice'));
const VinayakaMissionTherapistLoginPage = lazy(
  () => import('./components/auth/VinayakaMissionTherapistLoginPage'),
);
import VerifiedRoute from './components/auth/VerifiedRoute';

import './App.css';
import { useSEO } from './seo/useSEO';

const DEBUG_PARENT_ROUTE = false;

function parentRouteLog(...args: unknown[]) {
  if (!DEBUG_PARENT_ROUTE) return;
  console.log('[PARENT_ROUTE]', ...args);
}

/** Full-page pending verification (used when route is /pending-verification). */
function PendingVerificationPage() {
  const { user } = useAuth();
  return (
    <Suspense fallback={null}>
      <PendingVerificationNotice userRole={user?.role as string | undefined} />
    </Suspense>
  );
}

interface HubPayload {
  event: string;
  data?: unknown;
  message?: string;
}

/** Catches errors in a route so we show a message instead of a blank page */
class RouteErrorBoundary extends Component<{ children: ReactNode; fallback?: ReactNode }, { hasError: boolean; error?: Error }> {
  state = { hasError: false, error: undefined as Error | undefined };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{ padding: '2rem', fontFamily: 'system-ui', maxWidth: '480px', margin: '0 auto' }}>
          <h2 style={{ color: '#dc2626' }}>Something went wrong</h2>
          <p>{String(this.state.error.message)}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  /** Supersedes overlapping checkUserProfile runs (e.g. React Strict Mode, sign-in + init). */
  const checkUserProfileRunIdRef = useRef(0);
  /** Amplify v6 emits `signedIn`; legacy code also dispatches `signIn` — avoid double navigation. */
  const postSignInInFlightRef = useRef(false);

  useSEO(location.pathname);

  useEffect(() => {
    captureProgramReferralFromSearch(searchParams);
    const returnTo = searchParams.get('returnTo');
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      try {
        sessionStorage.setItem(POST_AUTH_RETURN_KEY, returnTo);
        localStorage.setItem(POST_AUTH_RETURN_KEY, returnTo);
      } catch {
        // ignore
      }
    }
    const redirect = searchParams.get('redirect');
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      try {
        sessionStorage.setItem(POST_AUTH_RETURN_KEY, redirect);
        localStorage.setItem(POST_AUTH_RETURN_KEY, redirect);
      } catch {
        // ignore
      }
    }
    // Assignment email sign-up: default new accounts to PARENT.
    const openAuth = searchParams.get('openAuth');
    if (
      openAuth === 'signUp' &&
      typeof returnTo === 'string' &&
      returnTo.startsWith('/questionnaire/')
    ) {
      try {
        localStorage.setItem('signupDefaultRole', 'PARENT');
      } catch {
        // ignore
      }
    }
  }, [searchParams]);

  const updateLastLogin = useCallback(async (userId: string) => {
    await touchLastLogin(userId);
  }, []);

  // Ensure a database User exists for the signed-in Cognito user
  const ensureUserRecord = useCallback(async (): Promise<string | null> => {
    let current;
    try {
      current = await getCurrentUser();
      if (!current || !current.userId) {
        return null;
      }
    } catch (error: any) {
      // Handle unauthenticated users gracefully
      if (error?.name === 'UserUnAuthenticatedException' || error?.message?.includes('not authenticated')) {
        return null;
      }
      throw error; // Re-throw other errors
    }
    
    try {
      // Using centralized client
      
      // Get email from ID token (works for both email/password and Google)
      const session = await fetchAuthSession();
      const idPayload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
      const emailRaw = (idPayload.email as string) || '';
      const email = emailRaw ? emailRaw.toLowerCase().trim() : '';
      const firstName = (idPayload.given_name as string) || '';
      const lastName = (idPayload.family_name as string) || '';

      const byId = await client.models.User.get({ id: current.userId }, { authMode: 'apiKey' });
      const emailRow = email ? await findBestUserRowByEmail(client, email) : null;
      const merged = pickUserRecordForSession(byId.data ?? null, emailRow);
      if (merged) {
        if (merged.id !== current.userId) {
          console.log('✅ ensureUserRecord: resolved User id', merged.id, '(Cognito sub', current.userId + ')');
        }
        return merged.id;
      }

      const pendingInvitationToken = localStorage.getItem('pendingInvitationToken');

      const createFromInvitation = async (invitation: {
        inviteeRole?: string | null;
        organizationId?: string | null;
        kidProfileId?: string | null;
        email?: string | null;
      }) =>
        ensureAppUserFromInvitation({
          cognitoUserId: current.userId,
          email,
          firstName,
          lastName,
          invitation,
          passwordMarker: 'google_auth_user',
        });

      if (pendingInvitationToken && email) {
        try {
          const invitations = await client.models.UserInvitation.list({
            filter: { token: { eq: pendingInvitationToken } },
            authMode: 'apiKey',
          });

          if (invitations.errors?.length) {
            console.error('❌ Invitation lookup errors:', invitations.errors);
          }

          const invitation =
            invitations.data?.find((inv) => inv.status !== 'REGISTERED') || invitations.data?.[0];
          if (invitation) {
            return createFromInvitation(invitation);
          }
        } catch (error) {
          console.error('❌ Error creating user record with invitation data:', error);
        }
      }

      if (email) {
        try {
          const invitationsByEmail = await client.models.UserInvitation.list({
            filter: { email: { eq: email } },
            authMode: 'apiKey',
          });

          const invitationByEmail =
            invitationsByEmail.data?.find((inv) => inv.status !== 'REGISTERED') ||
            invitationsByEmail.data?.[0];

          if (invitationByEmail) {
            return createFromInvitation(invitationByEmail);
          }
        } catch (error) {
          console.error('❌ Error creating user record from invitation email:', error);
        }
      }

      return await resolveExistingAppUserId(current.userId, email);
    } catch (error) {
      console.error('Error in ensureUserRecord:', error);
      return null;
    }
  }, []);

  const checkUserProfile = useCallback(async (userId: string) => {
    const runId = ++checkUserProfileRunIdRef.current;
    const go: typeof navigate = (...args) => {
      if (runId !== checkUserProfileRunIdRef.current) return;
      navigate(...args);
    };

    try {
      const currentPath = window.location.pathname;

      // Don't redirect if we're already on a protected route or social story view
      if (currentPath.includes('/parent-concerns') || 
          currentPath.includes('/questionnaire') ||
          currentPath.includes('/gap-analysis') ||
          currentPath.includes('/milestone-tasks') ||
          currentPath.includes('/daily-diary') ||
          currentPath.includes('/assessment-history') ||
          currentPath.includes('/games') ||
          currentPath === '/comprehension' ||
          currentPath === '/social-stories' ||
          currentPath.includes('/comprehension-levels') ||
          currentPath.includes('/blog') ||
          currentPath.includes('/community') ||
          currentPath.includes('/feedback') ||
          currentPath.includes('/refer-parent') ||
          currentPath.includes('/profile') ||
          currentPath.includes('/team-management') ||
          currentPath.includes('/provider-verification') ||
          currentPath.includes('/therapist-choose-plan') ||
          currentPath.includes('/pending-verification') ||
          currentPath.includes('/role-selection') ||
          currentPath.includes('/profile-setup') ||
          currentPath.includes('/social-stories/view/') ||
          currentPath.includes('/payment-success') ||
          currentPath.includes('/success') ||
          currentPath.includes('/pricing') ||
          currentPath.includes('/kid-profile') ||
          currentPath.includes('/goal-coach') ||
          currentPath.includes('/intake-form') ||
          currentPath.includes('/anaya-intake') ||
          currentPath.includes('/assessment-options') ||
          // Practice hub: avoid snatching users off these routes when auth/bootstrap re-runs checkUserProfile
          currentPath.includes('/client-plan-wizard') ||
          currentPath.includes('/parent-child-setup') ||
          currentPath.includes('/org-admin-dashboard') ||
          currentPath.includes('/client-control') ||
          currentPath.includes('/care-plan') ||
          currentPath.includes('/record-assessment') ||
          currentPath.includes('/therapy-goals') ||
          currentPath === '/book-meeting') {
        parentRouteLog('checkUserProfile: early return (already on protected path)', { path: currentPath });
        return;
      }

      const resolveInvitationRedirect = async () => {
        const storedToken = localStorage.getItem('pendingInvitationToken');
        const urlToken = new URLSearchParams(window.location.search).get('token');
        const token = storedToken || urlToken;
        
        // Try token first
        if (token) {
          try {
            const invitations = await client.models.UserInvitation.list({
              filter: { token: { eq: token } },
              authMode: 'apiKey'
            });
            const invitation = invitations.data?.[0];
            if (invitation && invitation.inviteeRole && invitation.organizationId) {
              try {
                await client.models.User.update({
                  id: userId,
                  role: invitation.inviteeRole,
                  organizationId: invitation.organizationId
                }, { authMode: 'apiKey' });
              } catch {
                // Non-blocking
              }

              if (invitation.inviteeRole === 'PARENT') {
                if (invitation.kidProfileId) {
                  go(`/kid-profile/${invitation.kidProfileId}`);
                } else {
                  go('/kid-profile-form');
                }
                return true;
              }

              go('/org-admin-dashboard');
              return true;
            }
          } catch {
            // Fall through to email lookup
          }
        }

        // Fallback: lookup invitation by email (for cache-cleared scenarios)
        try {
          const session = await fetchAuthSession();
          const idPayload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
          const email = (idPayload.email as string)?.toLowerCase()?.trim();
          if (!email) return false;

          const invitationsByEmail = await client.models.UserInvitation.list({
            filter: { email: { eq: email } },
            authMode: 'apiKey'
          });
          // Find therapist/org admin invitation (check all statuses, prefer non-REGISTERED)
          const invitation = invitationsByEmail.data?.find(inv => 
            (inv.inviteeRole === 'THERAPIST' || inv.inviteeRole === 'ORG_ADMIN' || inv.inviteeRole === 'CLINICIAN' || inv.inviteeRole === 'INTERNS') && inv.status !== 'REGISTERED'
          ) || invitationsByEmail.data?.find(inv => 
            inv.inviteeRole === 'THERAPIST' || inv.inviteeRole === 'ORG_ADMIN' || inv.inviteeRole === 'CLINICIAN' || inv.inviteeRole === 'INTERNS'
          ) || invitationsByEmail.data?.[0];
          
          if (invitation && invitation.inviteeRole && invitation.organizationId) {
            try {
              await client.models.User.update({
                id: userId,
                role: invitation.inviteeRole,
                organizationId: invitation.organizationId
              }, { authMode: 'apiKey' });
            } catch {
              // Non-blocking
            }

            if (invitation.inviteeRole === 'PARENT') {
              if (invitation.kidProfileId) {
                navigate(`/kid-profile/${invitation.kidProfileId}`);
              } else {
                navigate('/kid-profile-form');
              }
              return true;
            }

            navigate('/org-admin-dashboard');
            return true;
          }
        } catch {
          return false;
        }

        return false;
      };

      // Try to get user record with apiKey (most reliable)
      let userResponse = await client.models.User.get({ 
        id: userId
      }, { authMode: 'apiKey' });

      // If that fails, retry up to 2 times with increasing delays (handles timing issues after registration)
      if (!userResponse.data) {
        for (let attempt = 0; attempt < 2; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          userResponse = await client.models.User.get({ 
            id: userId
          }, { authMode: 'apiKey' });
          if (userResponse.data) break;
        }
      }

      if (!userResponse.data) {
        // Fallback: Get all users and find by ID client-side
        try {
          const allUsers = await client.models.User.list({ 
            limit: 1000,
            authMode: 'apiKey'
          });
          
          const user = allUsers.data?.find(u => u.id === userId);
          if (user) {
            userResponse.data = user;
          } else {
            // Last resort: try invitation redirect
            const redirected = await resolveInvitationRedirect();
            if (!redirected) {
              go('/profile-setup');
            }
            return;
          }
        } catch (fallbackError) {
          // Last resort: try invitation redirect
          const redirected = await resolveInvitationRedirect();
          if (!redirected) {
            go('/profile-setup');
          }
          return;
        }
      }

      // DEBUG: Log user role for troubleshooting
      console.log('🔍 checkUserProfile - User role:', userResponse.data.role, 'User ID:', userId, 'Email:', userResponse.data.email);
      console.log('🔍 checkUserProfile - Full user data:', JSON.stringify({
        id: userResponse.data.id,
        email: userResponse.data.email,
        role: userResponse.data.role,
        organizationId: userResponse.data.organizationId,
        fName: userResponse.data.fName,
        lName: userResponse.data.lName
      }));

      // SAFETY CHECK: If user has PARENT role but there's a therapist/org admin invitation, fix it immediately
      if (userResponse.data.role === 'PARENT' && userResponse.data.email) {
        try {
          const session = await fetchAuthSession();
          const idPayload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
          const email = (idPayload.email as string)?.toLowerCase()?.trim() || userResponse.data.email?.toLowerCase()?.trim();
          
          if (email) {
            const invitationsByEmail = await client.models.UserInvitation.list({
              filter: { email: { eq: email } },
              authMode: 'apiKey'
            });
            
            const therapistInvite = invitationsByEmail.data?.find(inv => 
              (inv.inviteeRole === 'THERAPIST' || inv.inviteeRole === 'ORG_ADMIN' || inv.inviteeRole === 'CLINICIAN' || inv.inviteeRole === 'INTERNS')
            );
            
            if (therapistInvite && therapistInvite.organizationId) {
              console.log('⚠️ FIXING ROLE: User has PARENT role but invitation says', therapistInvite.inviteeRole, '- updating...');
              await client.models.User.update({
                id: userId,
                role: therapistInvite.inviteeRole,
                organizationId: therapistInvite.organizationId
              }, { authMode: 'apiKey' });
              
              // Re-fetch user to get updated role
              const updatedUser = await client.models.User.get({ id: userId }, { authMode: 'apiKey' });
              if (updatedUser.data) {
                userResponse.data = updatedUser.data;
                console.log('✅ Role fixed! New role:', updatedUser.data.role);
              }
            }
          }
        } catch (fixError) {
          console.error('❌ Error fixing role:', fixError);
          // Continue with existing role
        }
      }

      // Auto-heal: if this record has no role, try resolving by email to the best org-linked record.
      if (!userResponse.data.role) {
        try {
          const session = await fetchAuthSession();
          const idPayload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
          const email = (idPayload.email as string)?.toLowerCase()?.trim();
          if (email) {
            const allUsers = await client.models.User.list({
              limit: 1000,
              authMode: 'apiKey'
            });
            const matchingByEmail = (allUsers.data || []).filter((u) =>
              u.email?.toLowerCase() === email
            );
            const preferredByRole = matchingByEmail.find((u) =>
              u.role === 'ORG_ADMIN' ||
              u.role === 'THERAPIST' ||
              u.role === 'CLINICIAN' ||
              u.role === 'INTERNS'
            );
            const preferredByOrg = matchingByEmail.find((u) => !!u.organizationId && !!u.role);
            const recovered = preferredByRole || preferredByOrg || null;
            if (recovered?.role) {
              userResponse.data = recovered;
              console.log('✅ Recovered role from email-linked record:', recovered.role, 'id:', recovered.id);
            }
          }
        } catch (recoverError) {
          console.warn('⚠️ Could not recover role by email:', recoverError);
        }
      }

      // Check if user has a role assigned
      if (!userResponse.data.role) {
        // Try to get role from invitation first
        const redirected = await resolveInvitationRedirect();
        if (redirected) {
          return;
        }
        // Only go to role-selection if no invitation found
        go('/role-selection');
        return;
      }

      if (isSchoolRole(userResponse.data.role)) {
        console.log('✅ Routing TEACHER/DAYCARE to Tinywins-school app');
        await redirectToSchoolApp();
        return;
      }

      if (userResponse.data.role === 'ADMIN' || userResponse.data.role === 'SME' || userResponse.data.role === 'SUPER_ADMIN') {
        go('/admin-dashboard');
        return;
      }

      // RULE 2: ORG_ADMIN → org dashboard, or choose-plan for freelancer without Stripe subscription
      if (userResponse.data.role === 'ORG_ADMIN') {
        const needsCheckout = await freelancerNeedsCheckout(
          client,
          userId,
          userResponse.data.organizationId
        );
        if (needsCheckout) {
          go('/therapist-choose-plan');
          return;
        }
        console.log('✅ Routing ORG_ADMIN to org-admin-dashboard');
        go('/org-admin-dashboard');
        return;
      }

      // TEST: CLINICIAN → tinywins-daycare (replace with SCHOOL_* roles later)
      if (shouldRedirectClinicianToDaycare(userResponse.data.role)) {
        console.log('✅ Routing CLINICIAN to tinywins-daycare');
        await redirectClinicianToDaycareApp();
        return;
      }

      // RULE 3: THERAPIST/INTERNS → freelancer onboarding: provider verification + plan before org dashboard
      if (userResponse.data.role === 'THERAPIST' || userResponse.data.role === 'INTERNS') {
        if (shouldRedirectTherapistToMiraPlus(userResponse.data.role)) {
          await redirectTherapistToMiraPlusWithSso('/');
          return;
        }
        if (userResponse.data.role === 'INTERNS' && userResponse.data.status !== 'ACTIVE') {
          go('/intern-pending');
          return;
        }
        // Normal or /therapists signup: no org until ProviderVerification creates FREELANCER org
        if (!userResponse.data.organizationId) {
          console.log('✅ Routing THERAPIST/INTERNS to /provider-verification (no organization yet)');
          go('/provider-verification');
          return;
        }
        const needsCheckout = await freelancerNeedsCheckout(
          client,
          userId,
          userResponse.data.organizationId
        );
        if (needsCheckout) {
          go('/therapist-choose-plan');
          return;
        }
        console.log('✅ Routing THERAPIST/INTERNS to org-admin-dashboard');
        console.log('✅ User details - Role:', userResponse.data.role, 'OrgId:', userResponse.data.organizationId);
        go('/org-admin-dashboard');
        return;
      }

      // RULE 1: PARENT → check kid profile → if none, create kid profile
      if (userResponse.data.role === 'PARENT') {
        // MiraPlus host: parents continue on TinyWins (shared Cognito SSO)
        if (shouldRedirectParentToTinyWins(userResponse.data.role)) {
          await redirectParentToTinyWinsWithSso('/');
          return;
        }
        const returnTo = getPostAuthReturnTo();
        const allowedReturnPaths = ['/comprehension', '/social-stories', '/community'];
        const isAllowedReturn =
          returnTo &&
          (allowedReturnPaths.includes(returnTo) ||
            (typeof returnTo === 'string' && returnTo.startsWith('/social-stories/')) ||
            (typeof returnTo === 'string' && returnTo.startsWith('/community/')) ||
            (typeof returnTo === 'string' && returnTo.startsWith('/questionnaire/')));
        if (isAllowedReturn && returnTo) {
          clearPostAuthReturnTo();
          go(returnTo);
          return;
        }
        console.log('✅ Routing PARENT to kid profile check');
        console.log('⚠️ WARNING: User has PARENT role - ID:', userId, 'Email:', userResponse.data.email);
        // Look up by Cognito sub AND App User.id (wizard historically mixed both as parentId).
        const kids = await listKidProfilesForParentSession();

        if (kids.length === 0) {
          go('/parent-child-setup');
        } else {
          try {
            const pendingAssignment = await getPendingAssignmentForParent({
              parentUserId: userId,
              parentEmail: userResponse.data.email,
              kidProfileIds: kids.map((k) => k.id),
            });
            if (runId !== checkUserProfileRunIdRef.current) return;
            if (pendingAssignment) {
              parentRouteLog('NAVIGATE → assigned questionnaire', {
                kidId: pendingAssignment.kidProfileId,
                assignmentId: pendingAssignment.id,
              });
              go(
                `/questionnaire/${pendingAssignment.kidProfileId}?assignmentId=${pendingAssignment.id}`,
              );
              return;
            }
          } catch (e) {
            console.warn('Pending assignment check failed:', e);
          }

          // Check if this is from check-delays flow
          const fromCheckDelays = localStorage.getItem('fromCheckDelays') === 'true';
          if (fromCheckDelays) {
            localStorage.removeItem('fromCheckDelays');
            go(`/parent-concerns/${kids[0].id}`);
            return;
          }
          // Default: parent home (kid picker / add kid), not goals or intake.
          parentRouteLog('NAVIGATE → kid-profile (parent dashboard)', { kidCount: kids.length });
          go('/kid-profile');
        }
        return;
      }

      // Default fallback - this should NOT happen if role is set correctly
      console.error('❌ UNEXPECTED: Falling through to default route. User role:', userResponse.data.role, 'User ID:', userId);
      console.error('❌ User data:', JSON.stringify(userResponse.data));
      // Instead of going to /dashboard (which redirects to kid-profile), go to role-selection
      go('/role-selection');
    } catch (error) {
      console.error('Error in checkUserProfile:', error);
      const redirected = await (async () => {
        try {
          const invitations = await client.models.UserInvitation.list({
            filter: { token: { eq: localStorage.getItem('pendingInvitationToken') || '' } },
            authMode: 'apiKey'
          });
          if (invitations.data && invitations.data.length > 0) {
            go('/org-admin-dashboard');
            return true;
          }
        } catch {
          return false;
        }
        return false;
      })();
      if (!redirected) {
        // After OAuth on mobile, checkUserProfile can fail (e.g. InvalidToken). Still send user to social story if that was their intent.
        const returnTo = getPostAuthReturnTo();
        const allowedReturnPaths = ['/comprehension', '/social-stories', '/community'];
        const isAllowedReturn =
          returnTo &&
          (allowedReturnPaths.includes(returnTo) ||
            (typeof returnTo === 'string' && returnTo.startsWith('/social-stories/')) ||
            (typeof returnTo === 'string' && returnTo.startsWith('/community/')));
        if (isAllowedReturn && returnTo) {
          clearPostAuthReturnTo();
          go(returnTo);
        } else {
          go('/profile-setup');
        }
      }
    }
  }, [navigate]);

  // Handle invitation association for existing Google users
  const handleInvitationAssociation = useCallback(async (userId: string, invitationToken: string) => {
    try {
      // Fetch invitation details
      const client = generateClient<Schema>();
      const invitations = await client.models.UserInvitation.list({
        filter: {
          token: { eq: invitationToken }
        }
      });

      if (invitations.data && invitations.data.length > 0) {
        const invitation = invitations.data[0];

        // Check if user was just created with invitation data (to avoid duplicate processing)
        const user = await client.models.User.get({ id: userId });
        if (user.data?.organizationId === invitation.organizationId && user.data?.role === invitation.inviteeRole) {
        } else {
          // Update user record with organization information
          const updateData: any = {
            id: userId,
            role: invitation.inviteeRole,
            organizationId: invitation.organizationId
          };

          await client.models.User.update(updateData);
        }

        // If this is a parent invitation with kid profile, associate the kid profile
        if (invitation.inviteeRole === 'PARENT' && invitation.kidProfileId) {
          await client.models.KidProfile.update({
            id: invitation.kidProfileId,
            parentId: userId,
            organizationId: invitation.organizationId
          } as any);
        }

        // Update invitation status to REGISTERED
        await client.models.UserInvitation.update({
          id: invitation.id,
          status: 'REGISTERED'
        });
        void import('./lib/careCircleNotifications').then(({ notifyOrgInviteAccepted }) =>
          notifyOrgInviteAccepted({
            organizationId: invitation.organizationId,
            organizationName: invitation.organizationName,
            newMemberUserId: userId,
            referrerId: invitation.referrerId,
            actorUserId: userId,
          }),
        );
      }
    } catch (error) {
      console.error('❌ Error handling invitation association:', error);
      // Don't throw error here as it shouldn't prevent login
    }
  }, []);

         const handleSignIn = useCallback(async () => {
           try {
             const user = await getCurrentUser();
             
             if (user) {
               // Check for pending invitation tokens BEFORE ensureUserRecord
               const pendingInvitationToken = localStorage.getItem('pendingInvitationToken');
               const pendingInvitationPage = localStorage.getItem('pendingInvitationPage');

               const pathAtSignIn = window.location.pathname;
               const keepCurrentPath =
                 pathAtSignIn.includes('/pricing') ||
                 pathAtSignIn.includes('/payment-success') ||
                 pathAtSignIn.includes('/success') ||
                 pathAtSignIn.includes('/social-stories/view/');

               const ensuredId = await ensureUserRecord();

               setIsAuthenticated(true);
               if (ensuredId) {
                 await updateLastLogin(ensuredId);
                 void tryAttributeProgramReferral(ensuredId);

                if (!keepCurrentPath && pendingInvitationToken && pendingInvitationPage) {
                   // Clear the stored invitation data
                   localStorage.removeItem('pendingInvitationToken');
                   localStorage.removeItem('pendingInvitationPage');
                   
                   // Handle invitation association for existing Google users
                   await handleInvitationAssociation(ensuredId, pendingInvitationToken);
                   
                   // Redirect based on user role
                   if (pendingInvitationPage === 'org-admin' || pendingInvitationPage === 'org-referral' || pendingInvitationPage === 'parent-referral') {
                     // Check invitation role
                     const invitations = await client.models.UserInvitation.list({
                       filter: {
                         token: { eq: pendingInvitationToken },
                         or: [
                           { status: { eq: 'PENDING' } },
                           { status: { eq: 'EMAIL_SENT' } }
                         ]
                       },
                       authMode: 'apiKey'
                     });
                     
                     if (invitations.data && invitations.data.length > 0) {
                       const invitation = invitations.data[0];
                       if (invitation.inviteeRole === 'PARENT') {
                         if (invitation.kidProfileId) {
                           navigate(`/kid-profile/${invitation.kidProfileId}`);
                         } else {
                           navigate('/kid-profile-form');
                         }
                         return;
                       } else if (invitation.inviteeRole === 'ORG_ADMIN') {
                         navigate('/org-admin-dashboard');
                         return;
                      } else if (invitation.inviteeRole === 'CLINICIAN') {
                        await redirectClinicianToDaycareApp();
                        return;
                      } else if (invitation.inviteeRole === 'THERAPIST' || invitation.inviteeRole === 'INTERNS') {
                        navigate('/org-admin-dashboard');
                        return;
                      }
                     }
                     
                     // Fallback - if we can't determine role, don't redirect (let checkUserProfile handle it)
                     return;
                   }
                 }
                 
                 // Check for redirect after login
                 const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
                 if (redirectAfterLogin) {
                   localStorage.removeItem('redirectAfterLogin');
                   navigate(redirectAfterLogin);
                   return;
                 }
                 
                 // Standard flow - check user profile (but skip if on social story view, payment-success, pricing, or comprehension-levels)
                 const currentPath = window.location.pathname;
                 if (!currentPath.includes('/social-stories/view/') && 
                    !currentPath.includes('/payment-success') &&
                    !currentPath.includes('/success') &&
                     !currentPath.includes('/pricing') &&
                     !currentPath.includes('/comprehension-levels')) {
                   await checkUserProfile(ensuredId);
                 } else {
                   console.log('🚫 Skipping checkUserProfile for path:', currentPath);
                 }
               } else {
                 
                 // Check if this is an invited user who should bypass profile setup
                if (!keepCurrentPath && pendingInvitationToken && pendingInvitationPage) {
                   // Clear the stored invitation data
                   localStorage.removeItem('pendingInvitationToken');
                   localStorage.removeItem('pendingInvitationPage');
                   
                   // Check invitation role for proper redirect (don't filter by status)
                   try {
                     const invitations = await client.models.UserInvitation.list({
                       filter: {
                         token: { eq: pendingInvitationToken }
                       },
                       authMode: 'apiKey'
                     });
                     
                     if (invitations.data && invitations.data.length > 0) {
                       const invitation = invitations.data[0];
                       if (invitation.inviteeRole === 'PARENT') {
                         if (invitation.kidProfileId) {
                           navigate(`/kid-profile/${invitation.kidProfileId}`);
                         } else {
                           navigate('/kid-profile-form');
                         }
                         return;
                       }
                     }
                   } catch (error) {
                     console.error('❌ Error checking invitation role:', error);
                   }
                   
                   // If this is an org invitation, go to org admin dashboard
                   if (pendingInvitationPage === 'org-admin' || pendingInvitationPage === 'org-referral' || pendingInvitationPage === 'parent-referral') {
                     navigate('/org-admin-dashboard');
                     return;
                   }
                 }
                 
                if (!keepCurrentPath) {
                  navigate('/profile-setup');
                }
               }
             }
           } catch (error) {
             console.error('🔐 handleSignIn - Error:', error);
             setIsAuthenticated(false);
           }
         }, [updateLastLogin, checkUserProfile, ensureUserRecord, navigate, handleInvitationAssociation]);

  const runPostSignInFromHub = useCallback(async () => {
    if (postSignInInFlightRef.current) return;
    postSignInInFlightRef.current = true;
    try {
      await handleSignIn();
    } finally {
      window.setTimeout(() => {
        postSignInInFlightRef.current = false;
      }, 2000);
    }
  }, [handleSignIn]);

  const checkUser = useCallback(async () => {
    if (hasInitialized) {
      return;
    }

    try {
      const user = await getCurrentUser();
      
      if (user && user.userId) {
        // Check for pending invitation tokens BEFORE ensureUserRecord
        const pendingInvitationToken = localStorage.getItem('pendingInvitationToken');
        const pendingInvitationPage = localStorage.getItem('pendingInvitationPage');

        const pathAtBootstrap = window.location.pathname;
        const keepCurrentPath =
          pathAtBootstrap.includes('/pricing') ||
          pathAtBootstrap.includes('/payment-success') ||
          pathAtBootstrap.includes('/success') ||
          pathAtBootstrap.includes('/social-stories/view/');

        const ensuredId = await ensureUserRecord();

        setIsAuthenticated(true);
        if (ensuredId) {
          void tryAttributeProgramReferral(ensuredId);
          // Check for pending invitation tokens from Google OAuth
          if (!keepCurrentPath && pendingInvitationToken && pendingInvitationPage) {
            // Clear the stored invitation data
            localStorage.removeItem('pendingInvitationToken');
            localStorage.removeItem('pendingInvitationPage');
            
            // Handle invitation association for existing Google users
            await handleInvitationAssociation(ensuredId, pendingInvitationToken);
            
            // Let checkUserProfile handle the redirect - it will find the associated kid profile
            // (but skip if on social story view, payment-success, or pricing)
            const currentPath = window.location.pathname;
            if (!currentPath.includes('/social-stories/view/') &&
                !currentPath.includes('/payment-success') &&
                !currentPath.includes('/success') &&
                !currentPath.includes('/pricing')) {
              await checkUserProfile(ensuredId);
            } else {
              console.log('🚫 Skipping checkUserProfile for path:', currentPath);
            }
            return;
          }
          
          // Check for check-delays flow (no invitation token needed)
          if (!keepCurrentPath && pendingInvitationPage === 'check-delays') {
            // Clear the stored page data
            localStorage.removeItem('pendingInvitationPage');
            
            // For existing users in check-delays flow, go directly to intake form
            
            // Get user's kid profiles and navigate directly to intake form
            try {
              const kidProfiles = await client.models.KidProfile.list({
                filter: { parentId: { eq: ensuredId } }
              });
              
              if (kidProfiles.data && kidProfiles.data.length > 0) {
                navigate(`/assessment-options/${kidProfiles.data[0].id}`);
              } else {
                localStorage.setItem('fromCheckDelays', 'true');
                navigate('/kid-profile-form');
              }
            } catch (error) {
              console.error('Error fetching kid profiles:', error);
              // Fallback to profile setup
              navigate('/profile-setup');
            }
            return;
          }
          
          // Check for redirect after login
          const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
          if (redirectAfterLogin) {
            localStorage.removeItem('redirectAfterLogin');
            navigate(redirectAfterLogin);
            return;
          }
          
          // Standard flow - check user profile (but skip if on social story view, payment-success, pricing, or comprehension-levels)
          const currentPath = window.location.pathname;
          if (!currentPath.includes('/social-stories/view/') &&
              !currentPath.includes('/payment-success') &&
              !currentPath.includes('/success') &&
              !currentPath.includes('/pricing') &&
              !currentPath.includes('/comprehension-levels')) {
            await checkUserProfile(ensuredId);
          } else {
            console.log('🚫 Skipping checkUserProfile for path:', currentPath);
          }
        } else {
          
          // Check if this is an invited user who should bypass profile setup
          if (!keepCurrentPath && pendingInvitationToken && pendingInvitationPage) {
            // Clear the stored invitation data
            localStorage.removeItem('pendingInvitationToken');
            localStorage.removeItem('pendingInvitationPage');
            
            // Check invitation role for proper redirect (don't filter by status)
            try {
              const invitations = await client.models.UserInvitation.list({
                filter: {
                  token: { eq: pendingInvitationToken }
                },
                authMode: 'apiKey'
              });
              
              if (invitations.data && invitations.data.length > 0) {
                const invitation = invitations.data[0];
                if (invitation.inviteeRole === 'PARENT') {
                  if (invitation.kidProfileId) {
                    navigate(`/kid-profile/${invitation.kidProfileId}`);
                  } else {
                    navigate('/kid-profile-form');
                  }
                  return;
                }
              }
            } catch (error) {
              console.error('❌ Error checking invitation role:', error);
            }
            
            // If this is an org invitation, go to org admin dashboard
            if (pendingInvitationPage === 'org-admin' || pendingInvitationPage === 'org-referral' || pendingInvitationPage === 'parent-referral') {
              navigate('/org-admin-dashboard');
              return;
            }
          }
          
          if (!keepCurrentPath) {
            navigate('/profile-setup');
          }
        }
      } else {
      }
    } catch (error: any) {
      // Handle unauthenticated users gracefully - this is expected when user is not logged in
      if (error?.name === 'UserUnAuthenticatedException' || error?.message?.includes('not authenticated')) {
        // User is not authenticated - this is normal, not an error
        setIsAuthenticated(false);
      } else {
        // Log other errors
        console.error('🔍 checkUser - Error:', error);
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
      setHasInitialized(true);
    }
  }, [checkUserProfile, hasInitialized, ensureUserRecord, navigate, handleInvitationAssociation]);

  useEffect(() => {
    checkUser();
    const listener = Hub.listen('auth', async ({ payload }: { payload: HubPayload }) => {
      switch (payload.event) {
        case 'signIn':
        case 'signedIn':
          await runPostSignInFromHub();
          break;
        case 'signOut':
        case 'signedOut':
          setIsAuthenticated(false);
          break;
        case 'signInWithRedirect':
          // Don't call handleSignIn here - let the signedIn event handle it
          break;
        case 'signInWithRedirect_failure':
          console.error('🔐 Hub listener - External sign-in failed:', payload.data);
          break;
               case 'customOAuthState':
                 // Handle custom OAuth state if needed
                 break;
      }
    });

    return () => {
      listener();
    };
  }, [checkUser, runPostSignInFromHub]);

  // Detect current language from localStorage (Google Translate now uses localStorage)
  const [currentLang, setCurrentLang] = useState(() => {
    // Initialize from localStorage if available
    try {
      const storedLangCode = localStorage.getItem('google-translate-language');
      if (storedLangCode) {
        // Map language codes to proper BCP 47 language tags
        const langMap: Record<string, string> = {
          'hi': 'hi-IN',
          'mr': 'mr-IN',
          'ta': 'ta-IN',
          'te': 'te-IN',
          'ur': 'ur-PK',
          'bn': 'bn-IN',
          'gu': 'gu-IN',
          'kn': 'kn-IN',
          'ml': 'ml-IN',
          'pa': 'pa-IN',
          'or': 'or-IN',
          'as': 'as-IN',
          'fr': 'fr-FR',
          'es': 'es-ES',
          'de': 'de-DE',
          'it': 'it-IT',
          'pt': 'pt-PT',
          'ru': 'ru-RU',
          'ja': 'ja-JP',
          'ko': 'ko-KR',
          'zh-CN': 'zh-CN',
          'ar': 'ar-SA',
        };
        return langMap[storedLangCode] || 'en-US';
      }
    } catch (error) {
      console.error('Error reading language from localStorage:', error);
    }
    return 'en-US';
  });
  
  useEffect(() => {
    const detectLanguage = () => {
      try {
        const storedLangCode = localStorage.getItem('google-translate-language');
        
        if (storedLangCode) {
          // Map language codes to proper BCP 47 language tags
          const langMap: Record<string, string> = {
            'hi': 'hi-IN',
            'mr': 'mr-IN',
            'ta': 'ta-IN',
            'te': 'te-IN',
            'ur': 'ur-PK',
            'bn': 'bn-IN',
            'gu': 'gu-IN',
            'kn': 'kn-IN',
            'ml': 'ml-IN',
            'pa': 'pa-IN',
            'or': 'or-IN',
            'as': 'as-IN',
            'fr': 'fr-FR',
            'es': 'es-ES',
            'de': 'de-DE',
            'it': 'it-IT',
            'pt': 'pt-PT',
            'ru': 'ru-RU',
            'ja': 'ja-JP',
            'ko': 'ko-KR',
            'zh-CN': 'zh-CN',
            'ar': 'ar-SA',
          };
          
          const detectedLang = langMap[storedLangCode] || 'en-US';
          setCurrentLang(detectedLang);
          return;
        }
      } catch (error) {
        console.error('Error reading language from localStorage:', error);
      }
      
      // Default to English if no language found
      setCurrentLang('en-US');
    };
    
    detectLanguage();
    
    // Listen for storage changes (when language is updated in GoogleTranslate component)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'google-translate-language') {
        detectLanguage();
      }
    };
    
    // Listen for custom events (from same tab)
    const handleCustomEvent = () => {
      detectLanguage();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChanged', handleCustomEvent);
    
    // Check for language changes periodically
    const interval = setInterval(detectLanguage, 1000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChanged', handleCustomEvent);
    };
  }, []);

  // Track URL changes
  useEffect(() => {
    // URL change tracking removed for performance
  }, [location.pathname]);

  // Non-logged-in users on ANY social story URL (list or direct view link) see the Social Stories gate — never the main landing.
  // Use initial path from index.html script if current path is / but initial was social-stories (server sent correct URL; client may have synced wrong).
  const initialPath = typeof window !== 'undefined' ? (window as unknown as { __INITIAL_PATH?: string }).__INITIAL_PATH : undefined;
  const effectivePath =
    location.pathname === '/' &&
    initialPath &&
    (initialPath === '/social-stories' || initialPath.startsWith('/social-stories/'))
      ? initialPath
      : location.pathname;
  // Path-based: /social-stories, /social-stories/view/xxx, or /social-stories/gate/xxx. Query: ?open=social-stories&story=slug. Hash (survives server redirect to /): #/social-stories or #/social-stories/view/xxx
  const openSS = searchParams.get('open') === 'social-stories';
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const hashIsSocialStories = hash === '#/social-stories' || hash.startsWith('#/social-stories/view/');
  const isSocialStoriesPath =
    effectivePath === '/social-stories' ||
    effectivePath.startsWith('/social-stories/') ||
    (location.pathname === '/' && openSS) ||
    (location.pathname === '/' && hashIsSocialStories);

  // When server sent us to / but referrer was a direct story link, rewrite to query form so gate shows and story is known
  useEffect(() => {
    if (location.pathname !== '/' || searchParams.get('open') === 'social-stories' || !document.referrer) return;
    try {
      const ref = document.referrer;
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      if (!origin || !ref.startsWith(origin) || !ref.includes('/social-stories/view/')) return;
      const m = ref.match(/\/social-stories\/view\/([^/?#]+)/);
      if (m?.[1]) navigate(`/?open=social-stories&story=${encodeURIComponent(m[1])}`, { replace: true });
    } catch {
      // ignore
    }
  }, [location.pathname, searchParams, navigate]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // If server kept URL but router shows /, sync URL back so gate and post-auth redirect use correct path
  if (!isAuthenticated && isSocialStoriesPath && location.pathname === '/' && initialPath && initialPath !== '/') {
    navigate(initialPath, { replace: true });
    return <div>Loading...</div>;
  }

  // Unauthenticated social-stories hub → marketing page; gate path → gate.
  // Story view / short slug URLs fall through to SocialStoryView (guest-readable, indexable).
  if (!isAuthenticated && isSocialStoriesPath) {
    const pathForSs = effectivePath || location.pathname;
    const isGatePath = pathForSs.startsWith('/social-stories/gate/');
    const isStoryContent =
      pathForSs.startsWith('/social-stories/view/') ||
      (pathForSs.startsWith('/social-stories/') &&
        !isGatePath &&
        pathForSs !== '/social-stories');
    const isHubOnly =
      (pathForSs === '/social-stories' || initialPath === '/social-stories') &&
      !searchParams.get('story') &&
      !hash.startsWith('#/social-stories/view/');

    if (isHubOnly) {
      return (
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>}>
          <SocialStoriesMarketingPage />
        </Suspense>
      );
    }
    if (!isStoryContent && (isGatePath || searchParams.get('story') || hash.startsWith('#/social-stories/view/'))) {
      return (
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>}>
          <SocialStoriesGatePage />
        </Suspense>
      );
    }
    // Story content paths fall through to <Routes> → SocialStoryView / short-path redirect
  }

  const hideParentHubChrome =
    location.pathname === '/' ||
    location.pathname === '/parents' ||
    location.pathname.startsWith('/parents/') ||
    location.pathname === '/community/new' ||
    /^\/community\/[^/]+\/edit\/?$/.test(location.pathname) ||
    location.pathname.startsWith('/assessment-history') ||
    location.pathname === VINAYAKA_MISSION_LOGIN_PATH ||
    location.pathname === '/schools-and-counties' ||
    location.pathname === '/developmental-milestone-tracker' ||
    location.pathname === '/iep-goal-coach' ||
    location.pathname === '/sensory-strategies' ||
    location.pathname.startsWith('/developmental-goals/') ||
    location.pathname === '/resources/iep' ||
    location.pathname === '/resources/communication' ||
    location.pathname === '/resources/sensory' ||
    location.pathname === '/resources/behavior-and-regulation' ||
    location.pathname === '/how-it-works' ||
    location.pathname === '/about' ||
    location.pathname === '/resources' ||
    location.pathname.startsWith('/resources/') ||
    ((!isAuthenticated || location.pathname.startsWith('/social-stories/view/') || location.pathname.startsWith('/social-stories/gate/')) &&
      (location.pathname === '/social-stories' || location.pathname.startsWith('/social-stories/')));

  return (
    <div className="app">
      {!hideParentHubChrome && <Header isAuthenticated={isAuthenticated} />}
      {/* Reserve fixed header (60px) + small gap so box-shadow does not cover first line of content (e.g. marketplace hero) */}
      {!hideParentHubChrome && <div style={{ height: 68, flexShrink: 0 }} aria-hidden />}
      <main className="main-content">
        <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>}>
        <Routes>
          {/* Home: parent-facing TinyWins marketing landing */}
          <Route path="/" element={<RouteErrorBoundary><TinyWinsHomeLanding /></RouteErrorBoundary>} />
          <Route path="/parent" element={<Navigate to="/parents" replace />} />
          <Route path="/parents" element={<ParentLandingPage />} />
          <Route path="/parents/blog/:slug" element={<ParentBlogArticlePage />} />
          <Route
            path="/community"
            element={
              isAuthenticated ? (
                <CommunityLayout>
                  <Suspense fallback={
                    <div className="flex min-h-[50vh] items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-600" />
                    </div>
                  }>
                    <CommunityFeedPage />
                  </Suspense>
                </CommunityLayout>
              ) : (
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-600" />
                  </div>
                }>
                  <CommunityFeedPage />
                </Suspense>
              )
            }
          />
          <Route
            path="/community/new"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Suspense fallback={
                  <div className="flex min-h-screen items-center justify-center bg-white">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
                  </div>
                }>
                  <CommunityEditorPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/:slug/edit"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Suspense fallback={
                  <div className="flex min-h-screen items-center justify-center bg-white">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
                  </div>
                }>
                  <CommunityEditorPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/u/:userId"
            element={
              isAuthenticated ? (
                <CommunityLayout>
                  <Suspense fallback={
                    <div className="flex min-h-[50vh] items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-600" />
                    </div>
                  }>
                    <CommunityAuthorPage />
                  </Suspense>
                </CommunityLayout>
              ) : (
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-600" />
                  </div>
                }>
                  <CommunityAuthorPage />
                </Suspense>
              )
            }
          />
          <Route
            path="/community/:slug"
            element={
              isAuthenticated ? (
                <CommunityLayout>
                  <Suspense fallback={
                    <div className="flex min-h-[50vh] items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-600" />
                    </div>
                  }>
                    <CommunityPostPage />
                  </Suspense>
                </CommunityLayout>
              ) : (
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-600" />
                  </div>
                }>
                  <CommunityPostPage />
                </Suspense>
              )
            }
          />
          <Route
            path="/organizations"
            element={
              isAuthenticated ? (
                <GlobalLayout>
                  <OrganizationDirectoryPage />
                </GlobalLayout>
              ) : (
                <OrganizationDirectoryPage />
              )
            }
          />
          <Route
            path="/organization/:organizationId/questions"
            element={<Navigate to=".." replace />}
          />
          <Route
            path="/organization/:organizationId"
            element={
              <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>}>
                <OrganizationPublicPage />
              </Suspense>
            }
          />
          <Route path="/therapists" element={<LandingTherapist />} />
          <Route path="/therapist" element={<Navigate to="/therapists" replace />} />
          <Route
            path={VINAYAKA_MISSION_LOGIN_PATH}
            element={
              <VinayakaMissionTherapistLoginPage isAuthenticated={isAuthenticated} />
            }
          />
          <Route path="/landing-3d" element={<RouteErrorBoundary><Landing3D /></RouteErrorBoundary>} />
          <Route path="/parent-referral" element={<LandingParentReferral />} />
          <Route
            path="/register"
            element={
              <RegisterWithProgramReferralRedirect />
            }
          />
          <Route path="/org-admin" element={<LandingOrgAdmin />} />
          <Route path="/org-referral" element={<LandingOrgReferral />} />
          <Route path="/contact" element={<ContactForm />} />
          <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/internships" element={<InternshipApplication />} />
          <Route path="/intern-pending" element={<InternPending />} />
          <Route path="/comprehension-levels" element={<ComprehensionLevels />} />
          <Route path="/comprehension" element={
            isAuthenticated ? (
              <GlobalLayout><ReadingComprehension /></GlobalLayout>
            ) : (
              <ComprehensionGatePage />
            )
          } />
          <Route path="/faq" element={<Navigate to="/how-it-works" replace />} />
          <Route path="/demo" element={<DemoDashboard />} />
          <Route
            path="/parent-goal-chat"
            element={
              <RouteErrorBoundary>
                {isAuthenticated ? (
                  <GlobalLayout>
                    <ParentGoalChat />
                  </GlobalLayout>
                ) : (
                  <ParentGoalChat />
                )}
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/goal-library-mockup"
            element={<Navigate to="/parent-goal-chat" replace />}
          />
          <Route
            path="/demo/goal-library-mockup"
            element={<Navigate to="/parent-goal-chat" replace />}
          />
          <Route path="/resources/visual-schedules" element={<ResourceCategoryPage category="visual-schedules" />} />
          <Route path="/resources/sensory-activities" element={<Navigate to="/resources/sensory" replace />} />
          <Route path="/resources/communication-tools" element={<Navigate to="/resources/communication" replace />} />
          <Route path="/resources/iep" element={<ResourceCategoryPage category="iep" />} />
          <Route path="/resources/communication" element={<ResourceCategoryPage category="communication" />} />
          <Route path="/resources/sensory" element={<ResourceCategoryPage category="sensory" />} />
          <Route
            path="/resources/behavior-and-regulation"
            element={<ResourceCategoryPage category="behavior-and-regulation" />}
          />
          <Route path="/schools-and-counties" element={<SchoolsAndCountiesPage />} />
          <Route path="/developmental-milestone-tracker" element={<DevelopmentalMilestoneTrackerPage />} />
          <Route path="/iep-goal-coach" element={<IepGoalCoachPage />} />
          <Route path="/sensory-strategies" element={<SensoryStrategiesPage />} />
          <Route
            path="/developmental-goals/communication"
            element={<DevelopmentalGoalCategoryPage category="communication" />}
          />
          <Route
            path="/developmental-goals/social-emotional"
            element={<DevelopmentalGoalCategoryPage category="social-emotional" />}
          />
          <Route
            path="/developmental-goals/daily-living"
            element={<DevelopmentalGoalCategoryPage category="daily-living" />}
          />
          <Route
            path="/developmental-goals/cognitive"
            element={<DevelopmentalGoalCategoryPage category="cognitive" />}
          />
          <Route
            path="/developmental-goals/motor-skills"
            element={<DevelopmentalGoalCategoryPage category="motor-skills" />}
          />
          <Route path="/questionnaire/demo" element={<QuestionnaireForm />} />
          <Route path="/questionnaire" element={<QuestionnaireForm />} />
          <Route path="/dashboard" element={
            isAuthenticated ? (
              <Navigate to="/kid-profile" replace />
            ) : (
              <Navigate to="/" replace />
            )
          } />
          <Route path="/profile-setup" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <ProfileSetup />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/kid-profile-form" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense
                  fallback={
                    <div className="flex min-h-[50vh] items-center justify-center px-4">
                      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="h-3 w-32 animate-pulse rounded bg-slate-200" />
                        <div className="mt-4 h-8 w-3/4 animate-pulse rounded bg-slate-200" />
                        <div className="mt-6 space-y-3">
                          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
                          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
                          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
                        </div>
                      </div>
                    </div>
                  }
                >
                  <CreateClientPlanWizard audience="parent" />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/parent-child-setup" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense
                  fallback={
                    <div className="flex min-h-[50vh] items-center justify-center px-4">
                      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="h-3 w-32 animate-pulse rounded bg-slate-200" />
                        <div className="mt-4 h-8 w-3/4 animate-pulse rounded bg-slate-200" />
                        <div className="mt-6 space-y-3">
                          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
                          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
                          <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
                        </div>
                      </div>
                    </div>
                  }
                >
                  <CreateClientPlanWizard audience="parent" />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/team-list" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><TeamList /></GlobalLayout></ProtectedRoute>} />
          <Route path="/team-management" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><TeamManagement /></GlobalLayout></ProtectedRoute>} />
          <Route path="/team-management/:kidProfileId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><TeamManagement /></GlobalLayout></ProtectedRoute>} />
          <Route path="/team-request" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><TeamRequest /></GlobalLayout></ProtectedRoute>} />
          <Route path="/kid-profile/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <ParentHomeDashboard />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/kid-team/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-fuchsia-200 border-t-fuchsia-600" />
                  </div>
                }>
                  <ParentKidTeamPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/messages/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-sky-200 border-t-sky-600" />
                  </div>
                }>
                  <MessagesInboxPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/goal-coach/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-200 border-t-amber-600" />
                  </div>
                }>
                  <GoalCoachPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/important-checklist" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
                  </div>
                }>
                  <ImportantChecklistPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/important-checklist/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
                  </div>
                }>
                  <ImportantChecklistPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/assignments" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
                  </div>
                }>
                  <ParentAssignmentsPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/assignments/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
                  </div>
                }>
                  <ParentAssignmentsPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/tips-and-tricks" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
                  </div>
                }>
                  <ParentStrategyBankPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <UserProfile />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/kid-profile" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <ParentHomeDashboard />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/parent-concerns/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <ParentConcernsForm 
                  onSubmit={() => {
                    // Empty callback to avoid interference
                  }}
                />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/questionnaire/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <QuestionnaireForm />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/anaya-intake/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <AnayaIntakeForm />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/intake-form/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <IntakeFormLanding />
              </GlobalLayout>
            </ProtectedRoute>
          } />

          <Route path="/games" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><GamesPage /></GlobalLayout></ProtectedRoute>} />
          <Route path="/games/copy-me" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><CopyMe /></GlobalLayout></ProtectedRoute>} />
          <Route path="/games/color-hunt" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><ColorHunt /></GlobalLayout></ProtectedRoute>} />
          <Route path="/games/what-happens-next" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><WhatHappensNext /></GlobalLayout></ProtectedRoute>} />
          <Route path="/games/complete-the-story" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><CompleteTheStory /></GlobalLayout></ProtectedRoute>} />
          <Route path="/games/reading-comprehension" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><ReadingComprehension /></GlobalLayout></ProtectedRoute>} />
          <Route path="/games/social-stories" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><SocialStoriesPage /></GlobalLayout></ProtectedRoute>} />
          <Route path="/games/open-up-cards" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><OpenUpCardsGame /></GlobalLayout></ProtectedRoute>} />

          <Route path="/social-stories" element={
            isAuthenticated ? (
              <GlobalLayout><SocialStories /></GlobalLayout>
            ) : (
              <SocialStoriesMarketingPage />
            )
          } />
          <Route path="/social-stories/view/:storySlug" element={<SocialStoryView />} />
          <Route path="/social-stories/gate/:storySlug" element={<SocialStoriesGatePage />} />
          <Route path="/social-stories/:storySlug" element={<SocialStoryShortPathRedirect />} />

          <Route path="/assessment" element={<GlobalLayout><Assessment /></GlobalLayout>} />
          <Route path="/check-delays" element={<CheckDelays />} />
          <Route path="/assessment-options/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <AssessmentOptions />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/gap-analysis" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <KidProfileHome />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/gap-analysis/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <GapAnalysisWrapper />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/milestone-tasks" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <KidProfileHome />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/milestone-tasks/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <MilestoneTableWrapper />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/milestone-path/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <KidMilestonePathPage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/add-milestone/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <AddMilestonePage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/milestone-details/:milestoneId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <MilestoneDetailsPage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/session-detail/:taskId/:sessionNumber/:milestoneId?" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <SessionDetailPage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/session-group/:sessionGroupId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <SessionGroupDetailPage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/session-history/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <SessionHistoryPage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/daily-diary" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <KidProfileHome />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/daily-diary/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <DailyDiaryWrapper />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/focus-areas" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
                  </div>
                }>
                  <ParentFocusAreasPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/focus-areas/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
                  </div>
                }>
                  <ParentFocusAreasPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/assessment-history" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <AssessmentHistoryPage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/assessment-history/:kidProfileId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <AssessmentHistoryPage />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/iep-analysis-results" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <IEPAnalysisResults />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          {/* Role Selection and Verification Routes */}
          <Route path="/role-selection" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <RoleSelection />
            </ProtectedRoute>
          } />
          <Route path="/provider-verification" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Suspense fallback={null}>
                <ProviderVerification />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/pending-verification" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <PendingVerificationPage />
            </ProtectedRoute>
          } />
          <Route path="/therapist-choose-plan" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <TherapistChoosePlanPage />
            </ProtectedRoute>
          } />

          <Route path="/admin-dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/org-admin-dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><OrgAdminDashboard /></GlobalLayout></ProtectedRoute>} />
          <Route path="/client-control/:kidProfileId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><TherapistClientDashboard /></GlobalLayout></ProtectedRoute>} />
          <Route path="/care-plan/:kidProfileId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><CarePlanPage /></GlobalLayout></ProtectedRoute>} />
          <Route path="/record-assessment/:kidProfileId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><AssessmentRecorder /></GlobalLayout></ProtectedRoute>} />
          <Route path="/client-plan-wizard" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><CreateClientPlanWizard /></GlobalLayout></ProtectedRoute>} />
          <Route path="/therapy-goals/:kidProfileId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><TherapyGoalSelectionPage /></GlobalLayout></ProtectedRoute>} />
          <Route path="/admin/org-usage/:orgId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><OrgUsageDetails /></ProtectedRoute>} />
          
          {/* Example: Therapist Dashboard with Verification Required */}
          <Route path="/therapist-dashboard" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <VerifiedRoute>
                <Suspense fallback={null}>
                  <GlobalLayout>
                    <TherapistDashboardPage />
                  </GlobalLayout>
                </Suspense>
              </VerifiedRoute>
            </ProtectedRoute>
          } />
          <Route path="/therapist-referrals" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={null}>
                  <TherapistReferralsPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/milestones/:kidProfileId" element={<AdminMilestoneView />} />
          <Route path="/admin/kid-milestones/:kidId" element={<AdminKidMilestones />} />
          <Route path="/admin/kid-profile/:kidProfileId" element={<ProtectedRoute isAuthenticated={isAuthenticated}><KidProfileHome /></ProtectedRoute>} />
          <Route path="/blog" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <BlogList />
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/blog/create" element={<GlobalLayout><CreateBlogPost /></GlobalLayout>} />
          <Route path="/blog/:slug" element={<GlobalLayout><BlogPostView /></GlobalLayout>} />
          <Route path="/feedback" element={<GlobalLayout><FeedbackForm /></GlobalLayout>} />
          <Route path="/refer-parent" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><ReferParentPage /></GlobalLayout></ProtectedRoute>} />
          <Route path="/program-referral" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <GlobalLayout>
                <Suspense fallback={
                  <div className="flex min-h-[50vh] items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
                  </div>
                }>
                  <ProgramReferralPage />
                </Suspense>
              </GlobalLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/dlm/master" element={<ProtectedRoute isAuthenticated={isAuthenticated}><DlmMasterManagement /></ProtectedRoute>} />
          <Route path="/admin/dlm/adjustments" element={<ProtectedRoute isAuthenticated={isAuthenticated}><DlmAdjustmentManagement /></ProtectedRoute>} />
          <Route path="/admin/milestone-catalog" element={<ProtectedRoute isAuthenticated={isAuthenticated}><GlobalLayout><MilestoneCatalogViewer /></GlobalLayout></ProtectedRoute>} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/team" element={<RouteErrorBoundary><TeamPage /></RouteErrorBoundary>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/support" element={<Support />} />
          <Route path="/pricing" element={<Navigate to="/contact" replace />} />
          <Route path="/pricing/" element={<Navigate to="/contact" replace />} />
          <Route
            path="/therapist-marketplace"
            element={
              isAuthenticated ? (
                <GlobalLayout>
                  <TherapistMarketplace />
                </GlobalLayout>
              ) : (
                <TherapistMarketplace />
              )
            }
          />
          <Route
            path="/therapist/:therapistId"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <GlobalLayout>
                  <TherapistProfile />
                </GlobalLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/book-meeting" element={<MeetingBooking />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-success/" element={<PaymentSuccess />} />
          <Route path="/success" element={<PaymentSuccess />} />
          <Route path="/success/" element={<PaymentSuccess />} />
          <Route path="/delete-my-account" element={<DeleteMyAccount />} />
          <Route path="/delete-my-data" element={<DeleteMyData />} />
          <Route path="*" element={
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4">404 - Page Not Found</Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                The page you're looking for doesn't exist.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate('/')}
                sx={{ mt: 2 }}
              >
                Go Home
              </Button>
            </Box>
          } />
        </Routes>
        </Suspense>
      </main>
      {location.pathname !== '/admin-dashboard' && <FooterModern />}
      <HelpChatOverlay />
    </div>
  );
};

const GapAnalysisWrapper = () => {
  const { kidProfileId } = useParams();
  if (!kidProfileId) {
    return <div>Error: No kid profile ID provided</div>;
  }
  return <GapAnalysis kidProfileId={kidProfileId} />;
};

const MilestoneTableWrapper = () => {
  const { kidProfileId } = useParams();
  if (!kidProfileId) {
    return <div>Error: No kid profile ID provided</div>;
  }
  return <MilestoneTable kidProfileId={kidProfileId} />;
};

const DailyDiaryWrapper = () => {
  return <DailyDiary />;
};

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <LanguageProvider>
        <BlogProvider>
          <AppContent />
        </BlogProvider>
      </LanguageProvider>
    </Router>
  );
};

export default App;
